// Camada tipada sobre o backend do wizard. Use os hooks de
// `useTanstackQuery` em cima desses helpers — não chame fetch direto
// dos componentes.
import { api } from './client';
import type { SportSlug } from '../lib/sportCatalog';

// ── Tipos compartilhados ─────────────────────────────────────────

export interface RaceEvent {
  id: string;
  name: string;
  slug: string | null;
  logoUrl: string | null;
  /// Capa/hero do evento — banner ~16:9 servido por S3 (ou data URL inline no
  /// fallback). Fundo do card no app (#191) e capa no live público. #190.
  heroImageUrl: string | null;
  primaryColor: string | null;
  startDate: string | null; // YYYY-MM-DD
  endDate: string | null;
  eventType: 'multi_day_categories' | 'single';
  scoringSystem: 'adjusted_pa' | 'direct_position' | 'absolute_time';
  /// Pós multi-day Fase 1: 'running' NÃO é valor válido — "em andamento"
  /// é derivado dos dias e chega em `hasRunningDay`. Manter 'running' aqui
  /// "por compat" escondeu do compilador um chip, um filtro e o botão
  /// Encerrar mortos na lista (#630). Rows antigas foram migradas na Fase 1.
  status: 'draft' | 'published' | 'finished';
  /// Derivado (não é coluna): algum raceDay do evento com status='running'.
  /// Preenchido em `GET /api/events/` e `GET /api/events/{id}`. **Respostas
  /// de escrita (POST/PUT) trazem `false` fixo** — o campo serializa sempre,
  /// mas só as leituras o calculam. Não derive status a partir do retorno de
  /// um PUT: recarregue pelo GET.
  hasRunningDay?: boolean;
  /// **DEPRECATED em favor de `raceDay.actualStartAt`** (multi-day Fase 1).
  /// Mantido como leitura pra rows antigas; novos disparos carimbam apenas
  /// `race_days.actual_start_at`. Pode vir não-null em eventos single-day
  /// migrados, sem garantia em multi-day.
  actualStartAt: string | null;
  /// #817 — visibilidade do RESULTADO APURADO. `true` (default) =
  /// comportamento de sempre; `false` = atleta, apoio e público param de ver
  /// posição final, pontuação e pódio, mas a apuração continua rodando e
  /// gravando, e as rotas de ranking do organizador seguem intactas. A
  /// colocação provisória ao vivo não é afetada. Editável durante a prova.
  ///
  /// Aditivo no PUT: **omitir não mexe no valor**. O wizard manda o campo só
  /// depois que o organizador toca no checkbox (`standingsVisibleTouched` em
  /// `Step1EventBasics`) — um form montado antes do corte carrega o valor
  /// velho, e reenviá-lo ao salvar outro campo republicaria a colocação pro
  /// público sem ninguém ter pedido. Caller novo que edite evento ao vivo
  /// tem a mesma obrigação: só mandar o que o usuário mexeu.
  standingsVisible?: boolean;
  /// #1172 — o evento aceita atletas com rastreador dedicado (GL320MG). Liga
  /// o vínculo rastreador ↔ inscrição no check-in e no painel ao vivo, e é o
  /// gate do listener: posição de rastreador num evento com isto desligado é
  /// descartada. Default `false`. Só o `verified_organizer` muda (a frota é
  /// da Wind Maps). Mesma disciplina do `standingsVisible`: omitir não mexe.
  trackersEnabled?: boolean;
}

export interface Category {
  id: string;
  eventId: string;
  name: string;
  groupLabel: string | null;
  // #433 — union derivado do catálogo (lib/sportCatalog.ts): esporte novo
  // entra no catálogo e este tipo acompanha sem caçar literais.
  modality: SportSlug;
  ageRangeMin: number | null;
  ageRangeMax: number | null;
  colorHex: string | null;
  countsForTeamScore: boolean;
  displayOrder: number;
  description: string | null;
}

export interface RaceDayWithSegments {
  id: string;
  name: string;
  date: string;
  scheduledStartAt: string | null;
  displayOrder: number;
  /// #798 — 'day' (um tiro pro dia, default) | 'segment' (tiro por frota).
  /// Imutável depois do 1º gun do dia (backend responde 409).
  startMode: 'day' | 'segment';
  /// 'upcoming' | 'running' | 'finished'. O painel usa pra gatear a anulacao
  /// de boia (#929): o backend exige dia encerrado.
  status: string;
  segments: Array<{
    id: string;
    name: string;
    segmentType: 'downwind' | 'upwind' | 'tt_race';
    distanceEstimateMeters: number | null;
    displayOrder: number;
    /// #938 — largada PREVISTA desta frota (agenda, ISO UTC). Editável a
    /// qualquer momento; não entra em cálculo de tempo nenhum.
    scheduledStartAt: string | null;
    /// Coluna crua do tiro de frota (#798), ISO UTC. Só é preenchida no modo
    /// `segment`; no modo `day` fica nula pra sempre. **Não use pra decidir se
    /// a frota largou**: use `gunAt`.
    actualStartAt: string | null;
    /// #938 — o tiro que VALE pra esta frota, resolvido no servidor pelo
    /// `RaceGunResolver` (o da frota no modo `segment`, o do dia no modo
    /// `day`, o legado do evento de um dia só). É ele que trava a troca de
    /// `segmentType` (409 `segment_started`). Backend antigo não manda →
    /// undefined, e a tela cai no `actualStartAt`.
    gunAt?: string | null;
    /// #930 — boias ATIVAS do percurso (não removidas, não anuladas). O passo
    /// Apoio decide por aqui se oferece "Equipe de raia". Opcional porque
    /// backend antigo não manda — e aí o gate cai pro critério anterior.
    courseMarkCount?: number;
    startLine: [number, number][] | null;
    finishLine: [number, number][] | null;
    categories: string[];
  }>;
}

/// Tipo físico do apoio — dirige o ícone do marker (mobile + web).
/// `land` = apoio em terra genérico (a pé/ponto fixo), distinto de `car`.
/// `unknown` = legado/não informado (ícone neutro, nunca "?").
/// Os cinco do #927 (evento na água) são tipo de veículo, não papel: quem
/// pilota a lancha ou está na comissão entra pelo mesmo fluxo de apoio.
/// Ícone e rótulo de cada um vivem em `@/lib/craftType`.
export type CraftType =
  | 'car'
  | 'jet_ski'
  | 'ambulance'
  | 'land'
  | 'kite_staff'
  | 'motorboat'
  | 'boat'
  | 'jangada'
  | 'race_committee'
  | 'unknown';

export interface SupportVehicle {
  id: string;
  // course_marker = Equipe de raia (marca/confirma boias por GPS, F2.02).
  vehicleType: 'org_support' | 'team_support' | 'course_marker';
  // Dimensão separada de vehicleType (categoria). Veículo físico do apoio.
  craftType: CraftType;
  operatorId: string | null;
  vehicleNumber: number;
  name: string;
  radiusMeters: number;
  lat: number | null;
  lng: number | null;
}

export interface EventZone {
  id: string;
  name: string;
  zoneType: 'authorized' | 'restricted';
  colorHex: string | null;
  points: [number, number][] | null;
}

export interface OperatorInvite {
  id: string;
  email: string;
  name: string;
  vehicleType: 'org_support' | 'team_support' | 'course_marker';
  vehicleId: string | null;
  token?: string;
  status: 'pending' | 'accepted' | 'approved' | 'rejected' | 'expired';
  createdAt: string;
  expiresAt: string;
  acceptedAt: string | null;
  operatorId: string | null;
  acceptUrl?: string;
  // #158 — estado do último envio do convite por email. 'none' = link
  // aberto (sem email). 'skipped' = provider não configurado (dev).
  emailStatus?: 'none' | 'sent' | 'failed' | 'skipped';
  emailSentAt?: string | null;
}

export interface OrgSupportDriverResponse {
  vehicleId: string;
  vehicleNumber: number;
  inviteId: string;
  name: string;
  status: OperatorInvite['status'];
  expiresAt: string;
  acceptUrl: string;
  emailStatus?: OperatorInvite['emailStatus'];
  emailSentAt?: string | null;
}

export interface OperatorTeam {
  teamId: string;
  operatorId: string;
  name: string;
  contactEmail: string | null;
  colorHex: string | null;
  verifiedAt: string | null;
  validationStatus: 'valid' | 'invalid' | 'pending';
  validationReason: string | null;
  athleteCount: number;
  categoryCount: number;
}

export interface ImportRow {
  email: string;
  name: string;
  category: string;
  operatorSlug?: string;
  bibNumber?: number;
}

export interface ImportRowResult {
  row: number;
  email?: string;
  name?: string;
  categoryId?: string;
  teamId?: string | null;
  bibNumber?: number;
  isUpdate: boolean;
  errors: string[];
}

export interface ImportResponse {
  dryRun: boolean;
  committed?: boolean;
  rows: ImportRowResult[];
  summary: { total: number; errors?: number; inserts: number; updates: number };
}

// ── Endpoints ────────────────────────────────────────────────────

export const eventsApi = {
  list: () => api<RaceEvent[]>('/api/events/'),
  get: (id: string) => api<RaceEvent>(`/api/events/${id}`),
  create: (input: { name: string }) =>
    api<RaceEvent>('/api/events/', { method: 'POST', body: { id: crypto.randomUUID(), ...input, status: 'draft' } }),
  update: (id: string, input: Partial<RaceEvent>) =>
    api<RaceEvent>(`/api/events/${id}`, { method: 'PUT', body: input }),
  checkSlug: (slug: string, excludeId?: string) =>
    api<{ available: boolean; reason?: string }>(
      `/api/events/check-slug?slug=${encodeURIComponent(slug)}${excludeId ? `&excludeId=${excludeId}` : ''}`,
    ),
  // Logo (F1.08): vai junto no PUT como `logoBase64` (data URL ou base64 puro)
  // ou `logoUrl` (CDN externa). Backend grava inline como data URL em
  // `race_events.logo_url`. String vazia em qualquer um remove o logo.
  setLogo: (id: string, base64: string) =>
    api<RaceEvent>(`/api/events/${id}`, { method: 'PUT', body: { logoBase64: base64 } }),
  clearLogo: (id: string) =>
    api<RaceEvent>(`/api/events/${id}`, { method: 'PUT', body: { logoBase64: '' } }),

  // Hero/capa (#190): banner ~16:9. Mesmo mecanismo do logo — `heroBase64`
  // (data URL ou base64 puro) ou `heroUrl` (CDN externa). Vazio remove.
  setHero: (id: string, base64: string) =>
    api<RaceEvent>(`/api/events/${id}`, { method: 'PUT', body: { heroBase64: base64 } }),
  clearHero: (id: string) =>
    api<RaceEvent>(`/api/events/${id}`, { method: 'PUT', body: { heroBase64: '' } }),

  /// Mass start LEGACY (compat). Dispara o primeiro RaceDay com
  /// status='upcoming' do evento. Clientes novos devem usar `startDay`
  /// diretamente. Mantido pra retrocompat.
  startRace: (id: string) =>
    api<{ eventId: string; status: string; actualStartAt: string }>(
      `/api/events/${id}/start`,
      { method: 'POST' },
    ),

  /// Multi-day Fase 1: dispara largada de UM dia específico. Carimba
  /// `race_days.actual_start_at` + `race_days.status='running'` e broadcasta
  /// `RaceDayStarted` via SignalR. 409 se dia já disparado, dia finalizado,
  /// ou evento em draft/finished.
  startDay: (eventId: string, dayId: string) =>
    api<{
      eventId: string;
      dayId: string;
      dayStatus: string;
      actualStartAt: string;
      eventStatus: string;
    }>(`/api/events/${eventId}/days/${dayId}/start`, { method: 'POST' }),

  /// #798 — dispara a largada de UMA frota (dia `startMode='segment'`).
  /// O 1º tiro do dia também transiciona o dia; os seguintes só carimbam o
  /// segmento e broadcastam `SegmentStarted`. 409: segment_already_started /
  /// day_already_finished / wrong_start_mode.
  startSegment: (eventId: string, dayId: string, segmentId: string) =>
    api<{
      eventId: string;
      dayId: string;
      segmentId: string;
      segmentActualStartAt: string;
      dayStatus: string;
      dayActualStartAt: string;
      firstGunOfDay: boolean;
    }>(`/api/events/${eventId}/days/${dayId}/segments/${segmentId}/start`, { method: 'POST' }),

  /// Multi-day Fase 1: encerra dia (marca status='finished'). Atletas
  /// que não cruzaram ficam sem FinishTime. Broadcast `RaceDayFinished`.
  finishDay: (eventId: string, dayId: string) =>
    api<{ eventId: string; dayId: string; dayStatus: string }>(
      `/api/events/${eventId}/days/${dayId}/finish`,
      { method: 'POST' },
    ),

  /// Transições de status diferentes de "disparar" usam o PUT existente.
  /// Pós multi-day Fase 1, status do evento é só draft|published|finished
  /// (running deixou de ser valor válido — vira derivado dos dias).
  setStatus: (id: string, status: RaceEvent['status']) =>
    api<RaceEvent>(`/api/events/${id}`, { method: 'PUT', body: { status } }),

  /// Hard delete. Cascade no backend apaga categorias, dias, segmentos,
  /// inscrições, veículos, zonas e convites; sessões de prova ficam órfãs
  /// (event_id null) preservando o histórico de GPS.
  remove: (id: string) => api<void>(`/api/events/${id}`, { method: 'DELETE' }),
};

/// Patrocinador do evento (#557) — logo + link opcional. Servido também no
/// snapshot público do `/live` (banner do live/replay/telão).
export interface EventSponsor {
  id: string;
  eventId: string;
  name: string;
  logoUrl: string | null;
  linkUrl: string | null;
  displayOrder: number;
}

/// Teto por evento — espelha `WizardEndpoints.MaxSponsorsPerEvent`. O banner
/// é peça de divulgação, não diretório; o backend devolve 409
/// `too_many_sponsors` acima disso.
export const MAX_SPONSORS_PER_EVENT = 12;

export const sponsorsApi = {
  list: (eventId: string) => api<EventSponsor[]>(`/api/events/${eventId}/sponsors`),
  // `logoBase64` (data URL ou base64 puro) ou `logoUrl` (CDN externa);
  // string vazia em qualquer um remove o logo. Mesma convenção do logo/hero.
  create: (
    eventId: string,
    input: { name: string; logoBase64?: string; logoUrl?: string; linkUrl?: string },
  ) => api<EventSponsor>(`/api/events/${eventId}/sponsors`, { method: 'POST', body: input }),
  update: (
    id: string,
    input: { name?: string; logoBase64?: string; logoUrl?: string; linkUrl?: string; displayOrder?: number },
  ) => api<EventSponsor>(`/api/sponsors/${id}`, { method: 'PATCH', body: input }),
  remove: (id: string) => api<void>(`/api/sponsors/${id}`, { method: 'DELETE' }),
  reorder: (eventId: string, order: string[]) =>
    api<void>(`/api/events/${eventId}/sponsors/order`, { method: 'PUT', body: { order } }),
};

export const categoriesApi = {
  list: (eventId: string) => api<Category[]>(`/api/events/${eventId}/categories`),
  create: (eventId: string, input: Partial<Category>) =>
    api<Category>(`/api/events/${eventId}/categories`, { method: 'POST', body: input }),
  update: (id: string, input: Partial<Category>) =>
    api<Category>(`/api/categories/${id}`, { method: 'PATCH', body: input }),
  remove: (id: string) => api<void>(`/api/categories/${id}`, { method: 'DELETE' }),
  reorder: (eventId: string, order: string[]) =>
    api<void>(`/api/events/${eventId}/categories/order`, { method: 'PUT', body: { order } }),
  applySertoesTemplate: (eventId: string) =>
    api<Category[]>(`/api/events/${eventId}/categories/template/sertoes`, { method: 'POST' }),
};

export const raceDaysApi = {
  listWithSegments: (eventId: string) => api<RaceDayWithSegments[]>(`/api/events/${eventId}/race-days`),
  create: (eventId: string, input: { name: string; date: string; scheduledStartAt?: string; startMode?: 'day' | 'segment' }) =>
    api<{ id: string }>(`/api/events/${eventId}/race-days`, { method: 'POST', body: input }),
  update: (
    id: string,
    // startMode: 409 start_mode_locked_after_gun depois do 1º tiro do dia.
    // #933 — `hideUntilOfficial` esconde o resultado apurado DESTE dia
    // enquanto ele for provisório; publicar revela. Aditivo como os demais:
    // campo ausente não mexe no valor.
    input: {
      name?: string;
      date?: string;
      scheduledStartAt?: string;
      /// #938 — apaga o horário previsto. Flag separada porque `null` e
      /// ausente chegam iguais no backend (PATCH aditivo).
      clearScheduledStartAt?: boolean;
      displayOrder?: number;
      startMode?: 'day' | 'segment';
      hideUntilOfficial?: boolean;
    },
  ) => api<{ id: string; hideUntilOfficial?: boolean }>(`/api/race-days/${id}`, {
    method: 'PATCH',
    body: input,
  }),
  remove: (id: string) => api<void>(`/api/race-days/${id}`, { method: 'DELETE' }),
};

export const segmentsApi = {
  create: (
    raceDayId: string,
    input: { name: string; segmentType?: string; distanceEstimateMeters?: number; categoryIds?: string[] },
  ) => api<{ id: string }>(`/api/race-days/${raceDayId}/segments`, { method: 'POST', body: input }),
  update: (
    id: string,
    // #938 — editar a frota depois de criada. Todos os campos são aditivos:
    // ausente não mexe no valor.
    //
    // `segmentType`: 409 `segment_started` depois do tiro que vale pra esta
    // frota (`gunAt`). O tipo não muda a apuração (a chegada vale com ou sem
    // boia), mas é o formato combinado da prova. Os demais campos continuam
    // editáveis com o gun dado.
    //
    // `scheduledStartAt`: ISO com offset; o backend normaliza pra UTC. É
    // AGENDA — não confundir com o gun, que não se escreve por aqui.
    input: {
      name?: string;
      segmentType?: string;
      distanceEstimateMeters?: number;
      displayOrder?: number;
      scheduledStartAt?: string;
      /// Apaga a largada prevista (mesmo motivo do `raceDaysApi.update`).
      clearScheduledStartAt?: boolean;
      /// Apaga a distância estimada. `distanceEstimateMeters` ≤ 0 é 400
      /// `invalid_distance`.
      clearDistanceEstimateMeters?: boolean;
      categoryIds?: string[];
    },
  ) => api<{ id: string }>(`/api/segments/${id}`, { method: 'PATCH', body: input }),
  remove: (id: string) => api<void>(`/api/segments/${id}`, { method: 'DELETE' }),
  setLine: (segmentId: string, kind: 'start' | 'finish', points: [number, number][]) =>
    api<void>(`/api/segments/${segmentId}/line`, { method: 'PUT', body: { kind, points } }),
  removeLine: (segmentId: string, kind: 'start' | 'finish') =>
    api<void>(`/api/segments/${segmentId}/line/${kind}`, { method: 'DELETE' }),
};

// F2.01 — boias intermediárias (TT race). Lado obrigatório + duas posições
// (planejada/confirmada). `status` é derivado no backend (CourseMarkStatus).
export type CourseMarkSide = 'port' | 'starboard' | 'either';
export type CourseMarkStatus =
  | 'planned'
  | 'confirmed'
  | 'confirmed_with_divergence'
  | 'removed'
  | 'annulled';

/// #929 — motivos de anulação. Conjunto FECHADO porque vira label de métrica
/// no backend (Invariante #6); o caso concreto vai na nota, que é texto livre.
export type AnnulReason = 'drifted' | 'misplaced' | 'committee_decision';

/// #930 — para que serve a boia. `rounding` = contorno de regata (TT Race);
/// `checkpoint` = ponto de passagem obrigatório de downwind.
///
/// É RÓTULO e DEFAULT, não regra: quem decide se a passagem conta é o
/// `roundingRadiusMeters`, e quem decide se o bordo é cobrado é o
/// `requiredSide`. O servidor escolhe o tipo pelo percurso quando o cliente
/// omite — ver `CourseMarkDefaults` no backend.
export type CourseMarkKind = 'rounding' | 'checkpoint';

export interface CourseMark {
  id: string;
  raceSegmentId: string;
  sequence: number;
  name: string;
  kind: CourseMarkKind;
  requiredSide: CourseMarkSide;
  roundingRadiusMeters: number;
  plannedLat: number | null;
  plannedLng: number | null;
  confirmedLat: number | null;
  confirmedLng: number | null;
  confirmedByUid: string | null;
  confirmedAt: string | null;
  confirmedAccuracyM: number | null;
  removed: boolean;
  /// #928 — auditoria da última edição manual. `updatedByName` vem resolvido
  /// do `user_profiles` pelo backend (uid cru não diz nada na tela) e é null
  /// quando quem editou não tem perfil; aí mostra-se só o "quando".
  updatedAt: string | null;
  updatedByName: string | null;
  /// #929 — anulação pós-prova. Preenchidos juntos ou todos null: o backend
  /// grava e zera as 4 colunas de uma vez.
  annulledAt: string | null;
  annulReason: AnnulReason | null;
  annulNote: string | null;
  annulledByName: string | null;
  /// Passagens de atletas já registradas nesta boia. É o que faz a tela saber,
  /// ANTES do clique, que apagar destruiria a evidência de um protesto — o
  /// DELETE responde 409 quando isto é > 0.
  roundingCount: number;
  status: CourseMarkStatus;
  divergenceMeters: number | null;
}

/// Uma passagem preservada — a evidência que a anulação não apaga (#929).
export interface CourseMarkRounding {
  id: string;
  registrationId: string;
  athleteName: string;
  bibNumber: number;
  roundedAtUtc: string;
  side: 'port' | 'starboard' | 'unknown';
  sideCorrect: boolean;
}

export interface CourseMarksResponse {
  marks: CourseMark[];
  marksTotal: number;
  marksConfirmed: number;
  courseReady: boolean;
}

export const marksApi = {
  list: (segmentId: string) => api<CourseMarksResponse>(`/api/segments/${segmentId}/marks`),
  create: (
    segmentId: string,
    input: { name?: string; requiredSide?: CourseMarkSide; roundingRadiusMeters?: number; planned?: [number, number] },
  ) => api<CourseMark>(`/api/segments/${segmentId}/marks`, { method: 'POST', body: input }),
  /// `replaceConfirmed` (#928): sem ele, mexer na coordenada de uma boia já
  /// confirmada por GPS responde **409 `confirmed_position_would_be_replaced`**
  /// em vez de apagar o ponto tomado no campo. A UI pergunta e reenvia com a
  /// flag; cliente antigo simplesmente leva o 409.
  patch: (
    segmentId: string,
    markId: string,
    input: {
      name?: string;
      requiredSide?: CourseMarkSide;
      roundingRadiusMeters?: number;
      planned?: [number, number];
      removed?: boolean;
      replaceConfirmed?: boolean;
    },
  ) => api<CourseMark>(`/api/segments/${segmentId}/marks/${markId}`, { method: 'PATCH', body: input }),
  /// ⚠️ Boia com passagem registrada responde **409 `mark_has_roundings`**
  /// (#929) — o DELETE apagaria os `course_mark_roundings` em cascata, que é
  /// a evidência do protesto. O caminho é `annul`.
  remove: (segmentId: string, markId: string) =>
    api<void>(`/api/segments/${segmentId}/marks/${markId}`, { method: 'DELETE' }),
  reorder: (segmentId: string, order: string[]) =>
    api<void>(`/api/segments/${segmentId}/marks/order`, { method: 'PUT', body: { order } }),
  /// #929 — tira a boia da apuração PRESERVANDO as passagens. Organizer-only
  /// (Equipe de raia leva 403) e idempotente: anular de novo não reescreve
  /// quem anulou nem quando.
  annul: (segmentId: string, markId: string, input: { reason: AnnulReason; note?: string }) =>
    api<CourseMark>(`/api/segments/${segmentId}/marks/${markId}/annul`, { method: 'POST', body: input }),
  /// Desfaz a anulação. O histórico do desfazer vive no log do servidor —
  /// não há tabela de auditoria de boia (aresta consciente do #929).
  unannul: (segmentId: string, markId: string, input: { note?: string }) =>
    api<CourseMark>(`/api/segments/${segmentId}/marks/${markId}/unannul`, { method: 'POST', body: input }),
  roundings: (segmentId: string, markId: string) =>
    api<{ roundings: CourseMarkRounding[]; total: number }>(
      `/api/segments/${segmentId}/marks/${markId}/roundings`,
    ),
  confirm: (segmentId: string, markId: string, input: { lat: number; lng: number; accuracyM?: number }) =>
    api<CourseMark>(`/api/segments/${segmentId}/marks/${markId}/confirm`, { method: 'POST', body: input }),
};

export const vehiclesApi = {
  list: (eventId: string) => api<SupportVehicle[]>(`/api/events/${eventId}/support-vehicles`),
  create: (eventId: string, input: Partial<SupportVehicle>) =>
    api<SupportVehicle>(`/api/events/${eventId}/support-vehicles`, { method: 'POST', body: input }),
  update: (id: string, input: Partial<SupportVehicle>) =>
    api<SupportVehicle>(`/api/support-vehicles/${id}`, { method: 'PATCH', body: input }),
  remove: (id: string) => api<void>(`/api/support-vehicles/${id}`, { method: 'DELETE' }),
};

export const zonesApi = {
  list: (eventId: string) => api<EventZone[]>(`/api/events/${eventId}/zones`),
  create: (
    eventId: string,
    input: { name: string; zoneType: 'authorized' | 'restricted'; points: [number, number][]; colorHex?: string },
  ) => api<{ id: string }>(`/api/events/${eventId}/zones`, { method: 'POST', body: input }),
  remove: (id: string) => api<void>(`/api/zones/${id}`, { method: 'DELETE' }),
};

export const invitesApi = {
  list: (eventId: string) => api<OperatorInvite[]>(`/api/events/${eventId}/invites`),
  create: (eventId: string, input: { email: string; name: string; vehicleType?: string }) =>
    api<OperatorInvite>(`/api/events/${eventId}/invites`, { method: 'POST', body: input }),
  approve: (id: string) => api<void>(`/api/invites/${id}/approve`, { method: 'POST' }),
  reject: (id: string) => api<void>(`/api/invites/${id}/reject`, { method: 'POST' }),
  // #158 — reenvia o convite de apoio por email reusando o mesmo token.
  resendEmail: (id: string) =>
    api<{ id: string; emailStatus: OperatorInvite['emailStatus']; emailSentAt: string | null }>(
      `/api/invites/${id}/resend-email`,
      { method: 'POST' },
    ),
  listOperators: (eventId: string) => api<OperatorTeam[]>(`/api/events/${eventId}/operators`),
};

/// Atalho do Step 4 — cadastra carro org_support + invite "aberto" em
/// uma única chamada. Retorna o `acceptUrl` pra mandar no WhatsApp.
export const orgSupportApi = {
  // `kind`: 'org_support' (apoio da org, default) ou 'course_marker'
  // (Equipe de raia — confirma boias por GPS no campo, F2.02).
  create: (
    eventId: string,
    input: {
      name: string;
      kind?: 'org_support' | 'course_marker';
      // Tipo físico (só pra org_support). Omitido em course_marker.
      craftType?: Exclude<CraftType, 'unknown'>;
      // Email do motorista — opcional; pré-vincula o invite e é o
      // destinatário do convite por email (#158).
      email?: string;
    },
  ) =>
    api<OrgSupportDriverResponse>(`/api/events/${eventId}/org-support-drivers`, {
      method: 'POST',
      body: input,
    }),
};

/// #837 — só `approved` é atleta do evento (roster, colocação, cruzamento).
/// `pending` chegou pelo link/QR e espera o organizador; `rejected` fica pra
/// auditoria e some de toda superfície de prova.
export type RegistrationStatus = 'approved' | 'pending' | 'rejected';
export type RegistrationSource = 'organizer' | 'signup';

export interface Registration {
  id: string;
  athleteName: string;
  athleteEmail: string;
  athleteUid: string | null;
  bibNumber: number;
  categoryId: string;
  teamId: string | null;
  createdAt: string;
  /// Campos do #837. Opcionais no tipo porque o backend antigo não os manda;
  /// `status` ausente = `approved` (era o único estado possível).
  status?: RegistrationStatus;
  source?: RegistrationSource;
  decidedAt?: string | null;
}

/// Estado efetivo — backend antigo sem o campo é `approved`.
export function registrationStatus(r: Registration): RegistrationStatus {
  return r.status ?? 'approved';
}

// ─── #837 — inscrição por link/QR ─────────────────────────────────

export type SignupMode = 'closed' | 'manual_approval' | 'auto_approve';

export interface SignupLink {
  eventId: string;
  /// Null = nunca gerado. Rotacionar troca o token e o QR antigo morre.
  token: string | null;
  signupMode: SignupMode;
  /// Caminho relativo (`/inscricao/{token}`); prefixe com `window.location.origin`.
  path: string | null;
  acceptsSignups: boolean;
  closedReason: 'event_finished' | 'mode_closed' | null;
}

export const signupApi = {
  get: (eventId: string) => api<SignupLink>(`/api/events/${eventId}/signup-link`),
  /// Sem `signupMode` na 1ª geração o backend abre em `manual_approval`.
  configure: (eventId: string, input: { signupMode?: SignupMode; rotate?: boolean }) =>
    api<SignupLink>(`/api/events/${eventId}/signup-link`, { method: 'POST', body: input }),
  approve: (id: string, bibNumber?: number | null) =>
    api<Registration>(`/api/registrations/${id}/approve`, {
      method: 'POST',
      body: bibNumber ? { bibNumber } : {},
    }),
  reject: (id: string) => api<Registration>(`/api/registrations/${id}/reject`, { method: 'POST', body: {} }),
  /// `ids` vazio = todas as pendentes do evento.
  approveMany: (eventId: string, ids?: string[]) =>
    api<{ approved: number; ids: string[] }>(`/api/events/${eventId}/registrations/approve`, {
      method: 'POST',
      body: { ids: ids ?? null },
    }),
};

export interface RegistrationInput {
  name: string;
  email: string;
  categoryId: string;
  teamId?: string | null;
  bibNumber?: number | null;
}

export const registrationsApi = {
  importPreview: (eventId: string, rows: ImportRow[]) =>
    api<ImportResponse>(`/api/events/${eventId}/registrations/import`, {
      method: 'POST',
      body: { rows, dryRun: true },
    }),
  importCommit: (eventId: string, rows: ImportRow[]) =>
    api<ImportResponse>(`/api/events/${eventId}/registrations/import`, {
      method: 'POST',
      body: { rows, dryRun: false },
    }),
  list: (eventId: string) => api<Registration[]>(`/api/events/${eventId}/registrations`),
  create: (eventId: string, input: RegistrationInput) =>
    api<Registration>(`/api/events/${eventId}/registrations`, { method: 'POST', body: input }),
  update: (id: string, input: Partial<RegistrationInput>) =>
    api<void>(`/api/registrations/${id}`, { method: 'PATCH', body: input }),
  remove: (id: string) => api<void>(`/api/registrations/${id}`, { method: 'DELETE' }),
};
